import { useState } from 'react';
import { Megaphone, Users, Briefcase, HardHat } from 'lucide-react';
import { palette, transition, fontFamily } from '../../../styles/tokens';
import type { AnnouncementScope } from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSend: (content: string, scope: AnnouncementScope) => Promise<{ success: boolean; error?: string }>;
  sending: boolean;
  onResult: (msg: string, type: 'success' | 'error') => void;
}

const SCOPES: { key: AnnouncementScope; label: string; sub: string; Icon: typeof Users }[] = [
  { key: 'all', label: 'Everyone', sub: 'All active team members', Icon: Users },
  { key: 'employees', label: 'Employees', sub: 'Employees only', Icon: Briefcase },
  { key: 'contractors', label: 'Contractors', sub: 'Contractors only', Icon: HardHat },
];

export function AnnouncementComposer({ isOpen, onClose, onSend, sending, onResult }: Props) {
  const [content, setContent] = useState('');
  const [scope, setScope] = useState<AnnouncementScope>('all');

  if (!isOpen) return null;

  const canSend = content.trim().length > 0 && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    const res = await onSend(content.trim(), scope);
    if (res.success) {
      onResult('Announcement sent', 'success');
      setContent('');
      setScope('all');
      onClose();
    } else {
      onResult(res.error ?? 'Failed to send announcement', 'error');
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 460, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28, fontFamily,
        animation: 'announceIn 200ms ease-out',
      }}>
        <style>{`@keyframes announceIn { from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }`}</style>

        {/* Icon */}
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.amberWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 16,
        }}>
          <Megaphone size={22} color={palette.amber} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>New announcement</div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: 18 }}>
          Broadcast a message to your team. Recipients can read it but can&apos;t reply.
        </div>

        {/* Scope picker */}
        <div style={{ fontSize: 12, fontWeight: 600, color: palette.g600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Send to
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {SCOPES.map(({ key, label, sub, Icon }) => {
            const active = scope === key;
            return (
              <button
                key={key}
                onClick={() => setScope(key)}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  padding: '12px 8px', borderRadius: 12, cursor: 'pointer', fontFamily,
                  border: `1.5px solid ${active ? palette.accent : palette.g200}`,
                  background: active ? palette.accentWash : palette.white,
                  transition: `all ${transition.fast}`,
                }}
              >
                <Icon size={18} color={active ? palette.accent : palette.g500} strokeWidth={1.9} />
                <span style={{ fontSize: 12.5, fontWeight: 600, color: active ? palette.accent : palette.g700 }}>{label}</span>
                <span style={{ fontSize: 10.5, color: palette.g400, textAlign: 'center', lineHeight: 1.3 }}>{sub}</span>
              </button>
            );
          })}
        </div>

        {/* Message */}
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Write your announcement..."
          rows={4}
          autoFocus
          style={{
            width: '100%', boxSizing: 'border-box', resize: 'vertical',
            border: `1px solid ${palette.g200}`, borderRadius: 12,
            padding: '12px 14px', fontSize: 14, lineHeight: 1.45,
            fontFamily, outline: 'none', color: palette.g800,
            minHeight: 90, maxHeight: 220, marginBottom: 22,
            transition: `border-color ${transition.fast}`,
          }}
          onFocus={e => (e.currentTarget.style.borderColor = palette.accent)}
          onBlur={e => (e.currentTarget.style.borderColor = palette.g200)}
        />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: 'pointer', fontFamily,
            transition: `background ${transition.fast}`,
          }}
            onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
            onMouseLeave={e => (e.currentTarget.style.background = palette.white)}
          >Cancel</button>
          <button onClick={handleSend} disabled={!canSend} style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: canSend ? palette.accent : palette.g300, color: palette.white,
            fontSize: 13.5, fontWeight: 600,
            cursor: canSend ? 'pointer' : 'not-allowed', fontFamily,
            transition: `background ${transition.fast}`,
          }}>
            <Megaphone size={15} strokeWidth={2.2} />
            {sending ? 'Sending...' : 'Send announcement'}
          </button>
        </div>
      </div>
    </>
  );
}
